import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
// `@app/*` is the per-tenant overridable import path: it resolves to
// `src/tenants/<TENANT>/<path>` when that file exists for the current build,
// else to `src/<path>` (the baseline). Use it for screens a client may want
// bespoke. Plain relative imports are NOT overridable. See vite-tenant-resolver.ts.
import { Layout } from '@app/components/layout';
import { useAuthStore } from './lib/auth-store';
import { restoreSession } from './lib/api';
import { captureRef } from './lib/ref';
import { useTenantTheme } from './lib/theme-boot';
import {
  useBrandDocumentTitle,
  useCommerceEnabled,
  useStorefrontContent,
  useTenantConfig,
} from './storefront/lib/hooks';
import { TENANT_ROUTES } from '@app/storefront/tenant-landing';
import { LoginPage } from './pages/login';
import { SignupPage } from './pages/signup';
import { DashboardPage } from '@app/pages/dashboard';
import { CodeVaultPage } from './pages/code-vault';
import { OrdersPage } from './pages/orders';
import { OrderDetailPage } from './pages/order-detail';
import { GenealogyPage } from './pages/genealogy';
import { InviteFriendsPage } from './pages/invite-friends';
import { LeadsPage } from './pages/leads';
import { CashOutPage } from './pages/cash-out';
import { WalletTopupPage } from './pages/wallet-topup';
import { AutoshipPage } from './pages/autoship';
import { TrainingVideosPage } from './pages/training-videos';
import { SubscribePage } from './pages/subscribe';
import { SpecialLinksPage } from './pages/special-links';
import { RedeemPage } from './pages/redeem';
import { ReplicatedPage } from './pages/replicated';
import { ReplicatedPreviewPage } from './pages/replicated-preview';
import { EarningsPage } from './pages/earnings';
import { SettingsPage } from './pages/settings';
import { NotificationsPage } from './pages/notifications';
import { MessagesInboxPage, MessageThreadPage } from './pages/messages';
import { AssistantPage } from './pages/assistant';
import { RequireActiveSlot } from './components/require-active-slot';
import { RequireSubscription } from './components/require-subscription';
import { RequirePasswordChange } from './components/require-password-change';
import { ChangePasswordPage } from './pages/change-password';
import { ImpersonatePage } from './pages/impersonate';

// Omni buyer storefront (the public landing + shopping surface). Lazy-loaded so
// a logged-out shopper pulls only the storefront chunk, not the whole portal.
const StorefrontHome = lazy(() =>
  import('./storefront/pages/home').then((m) => ({ default: m.HomePage })),
);
const StorefrontOfficial = lazy(() =>
  import('./storefront/pages/official-store').then((m) => ({ default: m.OfficialStorePage })),
);
const StorefrontShop = lazy(() =>
  import('./storefront/pages/shop').then((m) => ({ default: m.ShopPage })),
);
const StorefrontSearch = lazy(() =>
  import('./storefront/pages/search').then((m) => ({ default: m.SearchPage })),
);
const StorefrontProduct = lazy(() =>
  import('./storefront/pages/product').then((m) => ({ default: m.ProductPage })),
);
const StorefrontCart = lazy(() =>
  import('./storefront/pages/cart').then((m) => ({ default: m.CartPage })),
);
const StorefrontCheckout = lazy(() =>
  import('./storefront/pages/checkout').then((m) => ({ default: m.CheckoutPage })),
);
const StorefrontBuy = lazy(() =>
  import('./storefront/pages/buy').then((m) => ({ default: m.BuyFunnelPage })),
);
const StorefrontAbout = lazy(() =>
  import('./storefront/pages/about').then((m) => ({ default: m.AboutPage })),
);
const StorefrontBecomeMember = lazy(() =>
  import('./storefront/pages/become-member').then((m) => ({ default: m.BecomeMemberPage })),
);
const StorefrontSellOnOmni = lazy(() =>
  import('./storefront/pages/sell-on-omni').then((m) => ({ default: m.SellOnOmniPage })),
);
const StorefrontBecomeMerchant = lazy(() =>
  import('./storefront/pages/become-merchant').then((m) => ({ default: m.BecomeMerchantPage })),
);
const StorefrontFaq = lazy(() =>
  import('./storefront/pages/faq').then((m) => ({ default: m.FaqPage })),
);
const StorefrontPayment = lazy(() =>
  import('./storefront/pages/payment').then((m) => ({ default: m.PaymentPage })),
);
const StorefrontTrackOrder = lazy(() =>
  import('./storefront/pages/track-order').then((m) => ({ default: m.TrackOrderPage })),
);
const StorefrontFeedback = lazy(() =>
  import('./storefront/pages/feedback').then((m) => ({ default: m.FeedbackPage })),
);

function StorefrontFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f5f5f5] text-sm text-[#767676]">
      Loading…
    </div>
  );
}

function Storefront({ children }: { children: ReactNode }) {
  return <Suspense fallback={<StorefrontFallback />}>{children}</Suspense>;
}

/**
 * Phase 13: hydrate session from the refresh cookie on App mount. Shows a
 * brief splash while in flight so RequireAuth doesn't bounce a logged-in
 * user to /login on page reload.
 *
 * Also waits on the public tenant config, because `public_site_info_only`
 * decides WHICH routes get mounted at all. Both are one round-trip and run in
 * parallel, so the gate costs no extra wall-clock — and without it an info-only
 * tenant would paint the full storefront for a frame before collapsing to its
 * landing page.
 */
function useBootGate(): 'pending' | 'ready' {
  const [sessionReady, setSessionReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await restoreSession();
      if (!cancelled) setSessionReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  // `retry: 0` on this query means a failure settles immediately rather than
  // hanging the gate; `useCommerceEnabled` then falls back to commerce-on.
  const configSettled = useTenantConfig().isFetched;
  return sessionReady && configSettled ? 'ready' : 'pending';
}

/**
 * The ONE writer of the sponsor ref (`mlm.member.ref`, sessionStorage).
 *
 * Mounted app-wide rather than inside the storefront header, because the two
 * routes that actually carry a ref — `/r/:refCode[/:slug]` and `/signup?ref=`
 * — are NOT storefront routes. A prospect who opened a replicated link and then
 * browsed to the shop used to lose their sponsor entirely, so the
 * account-at-checkout signup submitted no sponsorCode and a `links_only` tenant
 * 403'd them (`signup_requires_referral_link`).
 *
 * Reads the ref from `?ref=` on any route, and from the first path segment of
 * `/r/…` (path params aren't visible above <Routes>). `captureRef` ignores
 * empty values, so navigating to a ref-less page never clears a stored one.
 */
function RefCapture() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    const fromQuery = new URLSearchParams(search).get('ref');
    const fromPath = /^\/r\/([^/]+)/.exec(pathname)?.[1];
    captureRef(fromQuery ?? (fromPath ? decodeURIComponent(fromPath) : null));
  }, [pathname, search]);
  return null;
}

/**
 * Public paths that already have a hardcoded route — a data-driven `layout` page
 * is only auto-mounted when its path ISN'T one of these (so the front door `/`,
 * the stable `/storefront`, and the transactional/info routes are never
 * double-registered). Param routes (`/product/:id`, `/r/:refCode`) can't collide
 * since layout page paths are static.
 */
const RESERVED_PATHS = new Set<string>([
  '/', '/login', '/signup', '/impersonate', '/storefront', '/c',
  '/official', '/shop', '/search', '/cart', '/checkout', '/buy',
  '/about', '/become-member', '/sell-on-omni', '/become-merchant',
  '/faq', '/payment', '/track', '/feedback',
]);

/**
 * The reserved set in INFO-ONLY mode. Nearly every hardcoded route is gone, so
 * the tenant is free to publish CMS pages at `/about`, `/faq`, `/contact` and
 * the rest — paths that would otherwise be shadowed by storefront routes that
 * no longer exist.
 */
const INFO_ONLY_RESERVED_PATHS = new Set<string>(['/', '/impersonate', '/__rp-preview']);

export function App() {
  useTenantTheme();
  useBrandDocumentTitle();
  // Data-driven storefront pages (Phase-3 landings / company / testimonials).
  // The default layout only declares `/storefront` (reserved), so this is empty
  // for an un-configured tenant — zero new routes, byte-identical.
  const { layout } = useStorefrontContent().data;
  const commerce = useCommerceEnabled();
  // Extra storefront routes: data-driven layout pages PLUS the bespoke tenant's
  // build-time TENANT_ROUTES (so a bespoke /gallery, /about-us, /contact mount
  // even when the stored layout predates them). Deduped, non-reserved only.
  const reserved = commerce ? RESERVED_PATHS : INFO_ONLY_RESERVED_PATHS;
  const layoutPaths = (layout?.pages ?? []).map((p) => p.path).filter((p): p is string => !!p);
  const extraPaths = Array.from(new Set([...layoutPaths, ...TENANT_ROUTES])).filter(
    (path) => path.startsWith('/') && !path.includes(':') && !reserved.has(path),
  );
  // An impersonated session is the super-admin escape hatch onto an info-only
  // box (the token is admin-issued, so this is not a public bypass). It lands on
  // /dashboard, so the portal has to stay mounted or the hatch dead-ends on the
  // catch-all redirect.
  const impersonating = !!useAuthStore((s) => s.user?.impersonator);
  const bootStatus = useBootGate();
  if (bootStatus === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Restoring session…
      </div>
    );
  }

  // ── Information-site-only mode ───────────────────────────────────────────
  // A REDUCED route tree, not per-route guards: a route that isn't mounted
  // can't leak, and the existing catch-all already redirects everything else to
  // the landing page. `/login`, `/signup`, the whole storefront and the member
  // portal simply don't exist here.
  if (!commerce && !impersonating) {
    return (
      <Routes>
        <Route path="/" element={<Storefront><StorefrontHome /></Storefront>} />
        {extraPaths.map((path) => (
          <Route
            key={path}
            path={path}
            element={<Storefront><StorefrontHome path={path} /></Storefront>}
          />
        ))}
        <Route path="/__rp-preview" element={<ReplicatedPreviewPage />} />
        <Route path="/impersonate" element={<ImpersonatePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <>
    <RefCapture />
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      {/* Public replicated-link landing page. When the tenant enables the
          custom page it renders here; otherwise it redirects to signup with the
          ref code preserved for sponsor attribution. */}
      {/* Device-accurate preview target for the admin page builder. Its own
          namespace so it can never be mistaken for a referral code; renders
          nothing until the admin posts a draft into it. */}
      <Route path="/__rp-preview" element={<ReplicatedPreviewPage />} />
      <Route path="/r/:refCode" element={<ReplicatedPage />} />
      {/* Named campaign page: same ref (sponsor attribution) + a page slug. */}
      <Route path="/r/:refCode/:slug" element={<ReplicatedPage />} />
      {/* Ref-LESS campaign page. A storefront shopper clicking a virtual product
          (`virtual_products_open_replicated_page`) has no ref in the URL, and
          `/r/<slug>` would be ambiguous with `/r/<refCode>` — two different
          lookups with no way to tell them apart. Own namespace instead; the
          page still picks up any stored ref for sponsor attribution. */}
      <Route path="/c/:slug" element={<ReplicatedPage />} />
      {/* QoL Slice 4 — admin opens this route in a new tab with ?token=…;
          the exchange sets the impersonation refresh cookie and forwards to
          the dashboard. Public so the token can be exchanged before the
          session exists. */}
      <Route path="/impersonate" element={<ImpersonatePage />} />

      {/* Public storefront (no auth) — the member app's landing + shopping
          surface. A present member session unlocks member pricing + real
          checkout; logged-out shoppers see public pricing + a representative
          confirmation. Each page carries its own brand header/footer. */}
      <Route path="/" element={<Storefront><StorefrontHome /></Storefront>} />
      {/* Stable storefront-home path. `/` serves the configurable front door
          (homePath); `/storefront` always serves the storefront home, so a
          landing-first tenant keeps the marketplace reachable here. */}
      <Route path="/storefront" element={<Storefront><StorefrontHome path="/storefront" /></Storefront>} />
      {/* Data-driven extra layout pages + bespoke tenant routes (Phase 3 / Layer-3). */}
      {extraPaths.map((path) => (
        <Route
          key={path}
          path={path}
          element={<Storefront><StorefrontHome path={path} /></Storefront>}
        />
      ))}
      <Route path="/official" element={<Storefront><StorefrontOfficial /></Storefront>} />
      <Route path="/shop" element={<Storefront><StorefrontShop /></Storefront>} />
      <Route path="/search" element={<Storefront><StorefrontSearch /></Storefront>} />
      <Route path="/product/:id" element={<Storefront><StorefrontProduct /></Storefront>} />
      <Route path="/cart" element={<Storefront><StorefrontCart /></Storefront>} />
      <Route path="/checkout" element={<Storefront><StorefrontCheckout /></Storefront>} />
      {/* "Register, then pay" funnel entry. PUBLIC: it redirects a signed-out
          visitor to signup carrying the product, and a signed-in one straight
          to checkout — see storefront/pages/buy.tsx. */}
      <Route path="/buy/:productId" element={<Storefront><StorefrontBuy /></Storefront>} />
      {/* Public informational / CMS pages (footer + header links). */}
      <Route path="/about" element={<Storefront><StorefrontAbout /></Storefront>} />
      <Route path="/become-member" element={<Storefront><StorefrontBecomeMember /></Storefront>} />
      <Route path="/sell-on-omni" element={<Storefront><StorefrontSellOnOmni /></Storefront>} />
      <Route path="/become-merchant" element={<Storefront><StorefrontBecomeMerchant /></Storefront>} />
      <Route path="/faq" element={<Storefront><StorefrontFaq /></Storefront>} />
      <Route path="/payment" element={<Storefront><StorefrontPayment /></Storefront>} />
      <Route path="/track" element={<Storefront><StorefrontTrackOrder /></Storefront>} />
      <Route path="/feedback" element={<Storefront><StorefrontFeedback /></Storefront>} />

      <Route element={<RequireAuth />}>
        {/* QoL Slice 2 — forced change-password screen. Rendered OUTSIDE the
            password-change guard so the user can actually reach it; INSIDE
            RequireAuth so an anonymous visitor still bounces to /login. */}
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route element={<RequirePasswordChange />}>
        <Route element={<Layout />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          {/* The in-app store is GONE — there is one store, the public
              storefront. `/store*` redirects so an old bookmark, an emailed
              link or a stale tab lands somewhere real instead of on the
              catch-all. */}
          <Route path="/store" element={<Navigate to="/" replace />} />
          <Route path="/store/checkout" element={<Navigate to="/checkout" replace />} />
          <Route path="/store/:id" element={<Navigate to="/" replace />} />
          <Route path="/code-vault" element={<CodeVaultPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:id" element={<OrderDetailPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/messages" element={<MessagesInboxPage />} />
          <Route path="/messages/:id" element={<MessageThreadPage />} />
          {/* AI Assistant — deliberately OUTSIDE RequireActiveSlot: works
              pre-slot (account tools just have less to show), and a tenant may
              later promote it to the primary surface. */}
          <Route path="/assistant" element={<AssistantPage />} />
          <Route path="/settings" element={<SettingsPage />} />

          <Route element={<RequireActiveSlot />}>
            <Route path="/genealogy" element={<GenealogyPage />} />
            <Route path="/invite" element={<InviteFriendsPage />} />
            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/cash-out" element={<CashOutPage />} />
            <Route path="/wallet/top-up" element={<WalletTopupPage />} />
            <Route path="/auto-ship" element={<AutoshipPage />} />
            <Route path="/training-videos" element={<TrainingVideosPage />} />
            <Route path="/redeem" element={<RedeemPage />} />
            <Route path="/earnings" element={<EarningsPage />} />
            <Route path="/subscribe" element={<SubscribePage />} />
          </Route>
          <Route element={<RequireSubscription />}>
            <Route path="/special-links" element={<SpecialLinksPage />} />
          </Route>
        </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function RequireAuth() {
  const token = useAuthStore((s) => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return <Outlet />;
}
